import { useEffect, useRef } from "react";
import {
  groupNavigation,
  groupNavigationChildren,
  type NavigationLink,
} from "../../../../shared/src/site-navigation";
import "../../../src/ui/navigation-menu.css";
import "../../../src/ui/floating.css";
import { AccordionItem } from "./accordion";
import "../../../src/ui/accordion.css";

/**
 * Website links and disclosure groups in one navigation surface. Native popover keeps every
 * link reachable without JavaScript; Plain anchors a compact popup to each group, while
 * Pill shares a resizing backplate. Pass only links and public pictures already allowed.
 */
export function NavigationMenu({
  id,
  items,
  currentPath,
  style = "plain",
  className = "desktop-nav",
}: {
  id: string;
  items: readonly NavigationLink[];
  currentPath?: string;
  style?: "plain" | "pill" | "underline" | "links";
  className?: string;
}) {
  const root = useRef<HTMLElement>(null);
  const compact = style !== "pill";
  const entries = groupNavigation(items);
  const key = JSON.stringify(items);
  useEffect(() => {
    let dispose: (() => void) | undefined;
    let cancelled = false;
    void import("../../../src/ui/navigation-menu").then(({ mountNavigationMenu }) => {
      if (!cancelled && root.current) dispose = mountNavigationMenu(root.current);
    });
    return () => {
      cancelled = true;
      dispose?.();
    };
  }, [key, style]);
  const active = (item: NavigationLink) =>
    item.current ||
    (currentPath && (currentPath === item.href || currentPath.startsWith(`${item.href}/`)));
  const links = (items: readonly NavigationLink[]) => (
    <ul className="ui-navigation-menu-links">
      {groupNavigationChildren(items).map((entry, index) => (
        <li key={index}>
          {entry.kind === "group" ? (
            <AccordionItem title={entry.label}>
              {entry.links.map((item) => (
                <a
                  key={item.href}
                  className="ui-navigation-menu-link"
                  href={item.href}
                  aria-current={active(item) ? "page" : undefined}
                >
                  {item.label}
                </a>
              ))}
            </AccordionItem>
          ) : (
            <a
              className="ui-navigation-menu-link"
              href={entry.link.href}
              aria-current={active(entry.link) ? "page" : undefined}
            >
              {entry.link.label}
            </a>
          )}
        </li>
      ))}
    </ul>
  );
  const list = (inside: boolean) => (
    <ul className="ui-navigation-menu-list" data-navigation-toolbar={inside ? "popup" : "main"}>
      {entries.map((entry, index) => (
        <li key={index} className="ui-navigation-menu-item">
          {entry.kind === "link" ? (
            <a
              className="ui-navigation-menu-link"
              href={entry.link.href}
              data-nav-index={index}
              aria-current={active(entry.link) ? "page" : undefined}
            >
              {entry.link.label}
            </a>
          ) : (
            <>
              <button
                type="button"
                className="ui-navigation-menu-trigger"
                data-nav-index={index}
                data-group-index={index}
                id={`${id}-trigger-${inside ? "popup-" : ""}${index}`}
                popoverTarget={compact ? `${id}-popup-${index}` : `${id}-popup`}
                aria-controls={compact ? `${id}-popup-${index}` : `${id}-panel-${index}`}
                aria-expanded="false"
              >
                {entry.label}
                <svg
                  className="ui-navigation-menu-icon"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  aria-hidden="true"
                >
                  <path
                    d="m3 4.5 3 3 3-3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.25"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              {compact && (
                <div
                  id={`${id}-popup-${index}`}
                  className="ui-navigation-menu-dropdown ui-floating"
                  data-dropdown-index={index}
                  popover="auto"
                  aria-labelledby={`${id}-trigger-${index}`}
                >
                  {links(entry.links)}
                </div>
              )}
            </>
          )}
        </li>
      ))}
    </ul>
  );
  return (
    <nav
      ref={root}
      className={`${className} ui-navigation-menu`}
      data-ui="navigation-menu"
      data-navigation-style={style}
      aria-label="Main navigation"
    >
      {list(false)}
      {style === "pill" && (
        <div id={`${id}-popup`} className="ui-navigation-menu-popup" popover="auto">
          <div className="ui-navigation-menu-backplate" aria-hidden="true" />
          {list(true)}
          <div className="ui-navigation-menu-viewport">
            {entries.map((entry, index) => {
              if (entry.kind !== "group") return null;
              const picture = entry.links.find((item) => item.image)?.image;
              return (
                <section
                  key={index}
                  id={`${id}-panel-${index}`}
                  className="ui-navigation-menu-content"
                  data-group-panel={index}
                  aria-labelledby={`${id}-trigger-${index}`}
                >
                  {links(entry.links)}
                  {picture && (
                    <img
                      className="ui-navigation-menu-preview"
                      src={picture}
                      alt=""
                      width="180"
                      height="108"
                      loading="lazy"
                      decoding="async"
                    />
                  )}
                </section>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
}

/** The same authorized navigation as native disclosures in a phone menu sheet. */
export function NavigationDisclosureLinks({ items }: { items: readonly NavigationLink[] }) {
  const link = (item: NavigationLink) => (
    <a
      href={item.href}
      key={item.href}
      aria-current={item.current ? "page" : undefined}
      data-nav-path={item.href.startsWith("/") ? item.href : undefined}
    >
      {item.label}
    </a>
  );
  return (
    <div className="ui-navigation-disclosures">
      {groupNavigation(items).map((entry, index) =>
        entry.kind === "link" ? (
          link(entry.link)
        ) : (
          <AccordionItem key={index} title={entry.label}>
            {groupNavigationChildren(entry.links).map((child, at) =>
              child.kind === "link" ? (
                link(child.link)
              ) : (
                <AccordionItem key={at} title={child.label}>
                  {child.links.map(link)}
                </AccordionItem>
              ),
            )}
          </AccordionItem>
        ),
      )}
    </div>
  );
}
